import { GUIDANCE, type Defect, type ValidationReport } from '@drawing2d/core';
import { STRINGS, type Lang } from '../i18n.js';

/**
 * What is wrong with the drawing, before anyone acts on the output.
 *
 * Garbage in, garbage out is the whole reason this surface exists. The gate
 * measures what the conversion computed; it cannot measure what the export
 * threw away before the file arrived. A drawing whose text was written as
 * outlines converts to a perfectly accurate DXF of a drawing with no text in
 * it, and every number this application prints about that run is correct and
 * beside the point.
 *
 * So each defect is shown with three things, in this order: what was
 * **measured** in this file, **why** a producer does it, and **what to do**.
 * The measurement first, because it is the part that is about their drawing
 * rather than about drawings in general — and because a reader who does not
 * believe the measurement will not act on the advice.
 */

const SEVERITY_LABEL = {
  blocking: STRINGS.checkSeverityBlocking,
  degrading: STRINGS.checkSeverityDegrading,
  advisory: STRINGS.checkSeverityAdvisory,
} as const;

const VERDICT_LABEL = {
  ready: STRINGS.checkReady,
  degraded: STRINGS.checkDegraded,
  refused: STRINGS.checkRefused,
} as const;

/**
 * One defect: the headline and the measurement always visible, the explanation
 * behind a fold.
 *
 * Everything here has to be *present* — a measurement nobody can inspect is not
 * a measurement — and almost none of it has to be *read* to decide what to do.
 * The first version showed the whole catalogue entry for every defect and took
 * the convert page from under two screens to 4.3, which is the U5 defect
 * exactly: the outcome pushed below the fold by a panel explaining itself at
 * length. The title says what is wrong, the measurement says it is about this
 * file, and the reader opens the rest when they want to act on it.
 */
function DefectRow({ defect, lang }: { defect: Defect; lang: Lang }): JSX.Element {
  const g = GUIDANCE[defect.kind];
  return (
    <li className={`defect ${defect.severity}`} data-defect={defect.kind}>
      <h4>
        {g.title[lang]}
        <span className="severity">{SEVERITY_LABEL[defect.severity][lang]}</span>
        {defect.pageIndex !== undefined && (
          <span className="page">
            {STRINGS.checkPage[lang]} {defect.pageIndex + 1}
          </span>
        )}
      </h4>

      {/* The numbers out of this file, never folded: they are the part that is
          about their drawing rather than about drawings in general. */}
      <p className="measured">{defect.measured}</p>

      <details className="fold" data-fold={`defect-${defect.kind}`}>
        <summary>{STRINGS.checkFix[lang]}</summary>
          <dl>
          <dt>{STRINGS.checkCause[lang]}</dt>
          <dd>{g.cause[lang]}</dd>

          <dt>{STRINGS.checkFix[lang]}</dt>
          <dd>{g.recommendation[lang]}</dd>
        </dl>

        {(g.autocad !== null || g.solidworks !== null) && (
          <ul className="settings">
            {g.autocad && (
              <li>
                <b>AutoCAD</b> — {g.autocad[lang]}
              </li>
            )}
            {g.solidworks && (
              <li>
                <b>SolidWorks</b> — {g.solidworks[lang]}
              </li>
            )}
          </ul>
        )}
      </details>
    </li>
  );
}

export function InputCheck({
  report,
  lang,
  onUploadAgain,
}: {
  report: ValidationReport;
  lang: Lang;
  /** Absent when there is nowhere to go back to, such as a bundled sample. */
  onUploadAgain?: () => void;
}): JSX.Element {
  // Open when the drawing was refused, folded when it merely converted with
  // loss. A refused drawing has nothing else on the page worth reading, so the
  // reason belongs in the way; a degraded one converted, and the headline is
  // what the reader needs — the catalogue in front of the result is the U5
  // defect, and the first version of this panel took the page from 1.7 screens
  // to 3.0 proving it.
  const open = report.verdict === 'refused';

  return (
    <section className="input-check" data-verdict={report.verdict}>
      <h3>{STRINGS.checkTitle[lang]}</h3>
      {/* The count is in the headline, so the one line a reader is guaranteed to
          see is quantitative rather than a mood. */}
      <p className="verdict">
        {VERDICT_LABEL[report.verdict][lang].replace('{n}', String(report.defects.length))}
      </p>

      {report.defects.length > 0 && (
        <details className="fold" data-fold="input-check" open={open}>
          <summary>
            {STRINGS.checkFound[lang]}
            <span className="fold-count">{report.defects.length}</span>
          </summary>

          <ul className="defects">
            {report.defects.map((d) => (
              <DefectRow key={`${d.kind}-${String(d.pageIndex ?? 'all')}`} defect={d} lang={lang} />
            ))}
          </ul>

          <p className="re-export">
            {/* The invitation is made only when it is true. Sending somebody
                back to their CAD application over a genuine spline wastes an
                afternoon and teaches them to ignore the next message. */}
            {report.reExportWouldHelp
              ? STRINGS.checkReExport[lang]
              : STRINGS.checkReExportNo[lang]}
          </p>

          {report.reExportWouldHelp && onUploadAgain && (
            <button type="button" className="upload-again" onClick={onUploadAgain}>
              {STRINGS.checkUpload[lang]}
            </button>
          )}
        </details>
      )}
    </section>
  );
}
